import { Fixtuur } from '@fixtuur/backpack';
import { Product as ProductService } from '@fixtuur/gift-client';
import * as O from 'fp-ts/Option';
import * as Hooks from 'preact/hooks';
import { useFeatureFlag } from './useFeatureFlag';
import { isPublishedEff } from './useParentApi/getPublishedStatus';
import { useSelector } from '../store/redux';
import * as Selectors from '../store/selectors';
import { useEffectful } from './useEffectful';
import { Effect, Array, Either } from 'effect';
import { useStellaLayer } from './useStellaLayer';

export function useInspirationProduct() {
    const stellaLayer = useStellaLayer();
    const tenant = useSelector(Selectors.Core.tenant);
    const shouldDisplayUnpublished = useSelector(Selectors.Initialisation.shouldDisplayUnpublished);

    const inspirationLaunchProductId = useSelector(
        Selectors.Initialisation.launchInspirationProductId,
    );
    const inspirationProductId = useSelector(Selectors.Initialisation.currentInspirationProductId);

    const configureDisabled = useFeatureFlag('configure-disabled');

    const inspirationStartingProduct = useEffectful(
        () =>
            inspirationLaunchProductId && tenant
                ? ProductService.ProductService.pipe(
                      Effect.andThen(productService =>
                          productService.getById(inspirationLaunchProductId, tenant),
                      ),
                      Effect.provide(stellaLayer),
                  )
                : Effect.succeed(null),
        [inspirationLaunchProductId, tenant, stellaLayer],
    ).result;

    const groupingCode =
        inspirationStartingProduct?.kind === 'simple' &&
        inspirationStartingProduct.groupingCode &&
        !configureDisabled
            ? inspirationStartingProduct.groupingCode
            : null;

    const inspirationProductGroup =
        useEffectful(
            () =>
                Effect.gen(function* () {
                    if (!groupingCode || !tenant) {
                        return null;
                    }
                    const productService = yield* ProductService.ProductService;
                    const apiVariants = yield* productService.getByGroupingCode(
                        tenant,
                        groupingCode,
                    );
                    const apiVariantsWithStatus = yield* Effect.all(
                        apiVariants.map(variant =>
                            isPublishedEff(variant.canonicalId, tenant, '3dxInspiration').pipe(
                                Effect.map(published => ({ variant, published })),
                            ),
                        ),
                        { concurrency: 20 },
                    );

                    const [excluded, includedVariants] = Array.partitionMap(
                        apiVariantsWithStatus,
                        ({ variant, published }) =>
                            shouldDisplayUnpublished || published
                                ? Either.right(variant)
                                : Either.left(variant),
                    );

                    if (excluded.length > 0) {
                        console.log(
                            `Manifest filtering out the following unpublished products:\n`,
                            excluded.map(p => `${p.canonicalId}: ${p.name}`).join('\n'),
                        );
                    }
                    const productGroup = Fixtuur.InspirationProduct.create({
                        variants: includedVariants.map(Fixtuur.Product.fromApi),
                    });
                    return productGroup;
                }).pipe(Effect.provide(stellaLayer)),
            [groupingCode, tenant, stellaLayer],
        ).result ?? null;

    const inspirationProduct = Hooks.useMemo(
        () =>
            inspirationProductId && inspirationProductGroup
                ? O.toNullable(
                      Fixtuur.InspirationProduct.productById(inspirationProductId)(
                          inspirationProductGroup,
                      ),
                  )
                : inspirationStartingProduct
                  ? Fixtuur.Product.fromApi(inspirationStartingProduct)
                  : null,
        [inspirationProductGroup, inspirationProductId, inspirationStartingProduct],
    );

    return { inspirationProduct, inspirationProductGroup, groupingCode, inspirationProductId };
}
